import React, { CSSProperties, useCallback, useMemo, useState } from 'react'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { TableColumnListColumnValue } from '@makyoapp/makyo-common'
import {
  ColorVariant,
  CornerRadius,
  DataTable,
  HoverEffectVariant,
  HoverIntensity,
  Spacing,
  TypographyValue,
} from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db'
import { DateTimeCell } from '@/core/models/columns/dateTime'
import { LookupCell } from '@/core/models/columns/lookup'
import { IRow } from '@/core/models/tables/row'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { cleanUpText } from '@/shared/utils'

import { CellComponent } from '../../shared/CellComponent'
import { getColBrief } from '../../utils'

export interface CompDataTableCoreProps {
  hasLabel: boolean
  pageSizes: number[]
  isPaginated: boolean
  footerAlignment: string
  tableSettings: {
    inProgress: boolean
    paginated: boolean
    stripped: boolean
    withHeader: boolean
    verticalBorder: boolean
    horizontalBorder: boolean
    checkbox: boolean
    sorting: boolean
    itemsPerPage: number[]
  }
  renderCompTitle: () => React.ReactNode
  columns: TableColumnListColumnValue[]
  table: IDBTable
  rowItems: IRow[]
  tableHeaderStyles: React.CSSProperties
  tableRowItemStyles: React.CSSProperties
  rowItemTypoStyle: React.CSSProperties
  onClick: (rowId: string) => void
  onItemSelect: (rowId: string, isChecked: boolean) => void
  listChecked: any
  onSelectAll: (rowIds: string[]) => void
  headlineTypoStyle: React.CSSProperties
  rowHeaderTypoStyle: React.CSSProperties
  footerTypoStyle: React.CSSProperties
  headerFooterColor: string
  oddBodyColor: string
  evenBodyColor: string
  borderColor: string
  showPagination: boolean
  showColumnToggle: boolean
  showGlobalFilter: boolean
  containerClient: any
  outlineBorderWidth: number
  tableShape: CornerRadius
  searchCorner: CornerRadius
  searchBoxWidth: string
  parentBackgroundColor: string
  spacingTable: Spacing
  searchColor: string
  searchColumnFilterColor: string
  maxLines: number
  renderCustomPagination: () => React.ReactNode
  setSortSetting: (sortSetting: { sortBy: string | null; sortType: 'asc' | 'desc' | null }) => void
  maxItems: number
  headlineTypo: TypographyValue
  rowHeaderTypo: TypographyValue
  rowItemTypo: TypographyValue
  footerTypo: TypographyValue

  // add button
  showAddButton: boolean
  addButtonColor: ColorVariant
  addButtonCornerRadius: CornerRadius
  addButtonPosition: 'inline' | 'bottom'
  handleAddButtonClick: () => void

  // hoverEffect: HoverEffectVariant
  // hoverIntensity: HoverIntensity
}

/**
 * CompDataTableCore
 *
 * @note pagination not implemented yet
 */
export const CompDataTableCore = ({
  table,
  rowItems,
  columns,
  tableSettings,
  renderCompTitle,
  rowItemTypoStyle,
  onClick,
  onItemSelect,
  listChecked,
  headlineTypoStyle,
  rowHeaderTypoStyle,
  footerTypoStyle,
  headerFooterColor,
  oddBodyColor,
  evenBodyColor,
  borderColor,
  showPagination,
  showColumnToggle,
  showGlobalFilter,
  outlineBorderWidth,
  tableShape,
  searchCorner,
  searchBoxWidth,
  parentBackgroundColor,
  spacingTable,
  searchColor,
  searchColumnFilterColor,
  maxLines,
  renderCustomPagination,
  setSortSetting,
  maxItems,
  hasLabel,
  headlineTypo,
  rowHeaderTypo,
  rowItemTypo,
  footerTypo,

  showAddButton,
  addButtonColor,
  addButtonCornerRadius,
  addButtonPosition,
  handleAddButtonClick,

  // hoverEffect,
  // hoverIntensity,
}: CompDataTableCoreProps) => {
  const isRowSelectionActive = tableSettings.checkbox === true

  const { isDevelopmentMode } = useAppBuilderContext()
  const [activeDevice] = useActiveDevice()

  const stripMarkdownAndTruncate = (markdown: string, maxLength: number = 70): string => {
    let plainText = markdown.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    plainText = plainText
      .replace(/[*_~`]+([^*_~`]+)[*_~`]+/g, '$1')
      .replace(/^#+\s+/gm, '')
      .replace(/^\s*>\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      .replace(/^\s*[-*_]{3,}\s*$/gm, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\n\s*\n/g, '\n')
      .trim()
    if (plainText.length > maxLength) {
      return plainText.slice(0, maxLength) + '...'
    }
    return plainText
  }

  const transformColumns = (
    inputJson: TableColumnListColumnValue[],
    oddBodyColor?: string,
    evenBodyColor?: string,
    sorting: boolean = false
  ) => {
    return inputJson
      .filter((item) => item.visibility?.[activeDevice] && item.label && item.label.trim() !== '')
      .map((item, i) => {
        const itemCol = getColBrief(table, item.col)

        let colType = itemCol?._Type
        const backgroundColor = i % 2 === 0 ? oddBodyColor : evenBodyColor

        return {
          accessorKey: item.col,
          header: item.label,
          cell: ({ row }: any) => {
            const rowId = row.original._id
            const value = row.original[item.col as string]
            const cleanValue = cleanUpText(value?.toString())
            const isLookup = colType === ColumnTypes.Lookup
            const isNumber = colType === ColumnTypes.Number
            let cellValue = isNumber ? value?.toString() : value
            let lookupColType = null

            if (!value) return null

            if (isLookup && value instanceof LookupCell) {
              lookupColType = value.resolvedType
              const lookupCell = value as LookupCell
              cellValue = lookupCell?.toDisplayString() || ''

              if (lookupColType === ColumnTypes.Image || lookupColType === ColumnTypes.Icon) {
                colType = lookupColType
              }
            }

            let dateTimeCell = null
            if (colType === ColumnTypes.DateTime) {
              dateTimeCell = new DateTimeCell(value, itemCol?._Settings as any, table.settings._Tz)
            }

            const isURL = colType === ColumnTypes.URL

            const maxVisible = 4
            let remainingCount = 0
            let visibleImages = []
            if (colType === ColumnTypes.ImageArray) {
              remainingCount = value?.length > maxVisible ? value?.length - maxVisible : 0
              visibleImages = value?.length > maxVisible ? value?.slice(0, maxVisible) : value
            }
            return (
              <div
                onClick={() => !isURL && onClick(rowId)}
                className='overflow-hidden text-ellipsis whitespace-nowrap'
                style={{
                  ...(maxLines > 1
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: maxLines,
                        WebkitBoxOrient: 'vertical',
                        whiteSpace: 'normal',
                      }
                    : {}),
                }}
              >
                {colType === ColumnTypes.Rating ||
                colType === ColumnTypes.Attachment ||
                colType === ColumnTypes.Image ||
                colType === ColumnTypes.Icon ||
                colType === ColumnTypes.Number ||
                (isLookup &&
                  (lookupColType === ColumnTypes.Rating ||
                    lookupColType === ColumnTypes.Attachment ||
                    lookupColType === ColumnTypes.Image ||
                    lookupColType === ColumnTypes.Icon ||
                    lookupColType === ColumnTypes.Number ||
                    lookupColType === ColumnTypes.Text)) ? (
                  <CellComponent
                    settings={itemCol?._Settings}
                    fileName={value}
                    cell={cellValue}
                    type={colType as ColumnTypes}
                    backgroundColor={backgroundColor as any}
                    classNameImage='mx-auto h-[50px] w-auto aspect-square object-cover rounded-sm border'
                    iconProps={{ size: 20 }}
                  />
                ) : colType === ColumnTypes.RichText ? (
                  stripMarkdownAndTruncate(cleanValue)
                ) : colType === ColumnTypes.URL ? (
                  <a href={cleanValue} target='_blank' rel='noopener noreferrer'>
                    {cleanValue}
                  </a>
                ) : colType === ColumnTypes.DateTime ? (
                  dateTimeCell?.toString()
                ) : colType === ColumnTypes.ImageArray ? (
                  <div className='flex flex-nowrap gap-1'>
                    {visibleImages.map((item: any, index: number) => (
                      <div
                        key={item.Url}
                        className='w-10 h-10 shrink-0 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center relative'
                        style={{
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        <img
                          src={item.Url}
                          alt={`image-${index}`}
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ))}
                    {remainingCount > 0 && (
                      <div className='w-10 h-10 rounded-md overflow-hidden bg-gray-200 flex items-center justify-center relative'>
                        <div className='absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center'>
                          <span className='text-white font-bold text-lg'>+{remainingCount}</span>
                        </div>
                        <img
                          src={value[maxVisible].Url}
                          alt='remaining-preview'
                          className='w-full h-full object-cover opacity-40'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  cleanValue
                )}
              </div>
            )
          },
          meta: {
            columnType: itemCol?._Type,
            tooltipEnabled: false,
          },
          enableSorting: sorting,
        }
      })
  }

  const filterData = (rawData: any, columns: any) => {
    const accessorKeys = columns.map((col: any) => col.accessorKey)

    return rawData.map((item: any) => {
      const filteredItem: any = {}
      filteredItem._id = item._id
      accessorKeys.forEach((key: any) => {
        if (item[key] !== undefined) {
          filteredItem[key] = item[key]
        } else {
          filteredItem[key] = null
        }
      })

      return filteredItem
    })
  }

  const titleTypography = headlineTypo // extractFontName(headlineTypoStyle?.font as string) ?? ''
  const headerTypography = rowHeaderTypo // extractFontName(rowHeaderTypoStyle?.font as string) ?? ''
  const footerTypography = footerTypo // extractFontName(footerTypoStyle?.font as string) ?? ''
  const bodyTypography = rowItemTypo // extractFontName(rowItemTypoStyle?.font as string) ?? ''

  const transformedColumns = transformColumns(columns, oddBodyColor, evenBodyColor, tableSettings.sorting) as any[]
  const filteredData = filterData(rowItems, transformedColumns)
  const borderWidth = outlineBorderWidth ? `${outlineBorderWidth}px` : '0px'

  const convertToPhysicalMargin = (margin: CSSProperties): Spacing => {
    return {
      top: margin.marginTop,
      right: margin.marginInlineEnd,
      bottom: margin.marginBottom,
      left: margin.marginInlineStart,
    }
  }

  const [rowIdToIndexMap, setRowIdToIndexMap] = useState<any>({})
  const getControlledSelection = useMemo(() => {
    if (!listChecked?.items || !Array.isArray(listChecked.items) || !rowIdToIndexMap) {
      return {}
    }
    return listChecked.items.reduce((acc: any, id: string) => {
      const rowIndex = rowIdToIndexMap[id]
      if (rowIndex !== undefined) {
        acc[rowIndex] = true
      }
      return acc
    }, {})
  }, [listChecked?.items, rowIdToIndexMap])

  const handleRowMappingChange = useCallback(
    (mapping: any) => {
      if (JSON.stringify(mapping) !== JSON.stringify(rowIdToIndexMap)) {
        setRowIdToIndexMap(mapping)
      }
    },
    [rowIdToIndexMap]
  )

  const handleSortChange = useCallback((sortBy: string | null, sortType: 'asc' | 'desc' | null) => {
    setSortSetting({ sortBy: sortBy || '', sortType: sortType || 'asc' })
  }, [])

  return (
    <div className='w-full flex'>
      <DataTable
        columns={transformedColumns}
        data={filteredData}
        headerFooterColor={headerFooterColor}
        oddRowColor={oddBodyColor}
        evenRowColor={evenBodyColor}
        borderColor={borderColor}
        verticalBorder={tableSettings.verticalBorder}
        horizontalBorder={tableSettings.horizontalBorder}
        borderWidth={borderWidth}
        pageSize={maxItems}
        width='100%'
        cornerRadius={tableShape}
        align={'start'}
        titleTypography={titleTypography as any}
        headerTypography={headerTypography as any}
        footerTypography={footerTypography as any}
        bodyTypography={bodyTypography as any}
        showPagination={showPagination}
        showColumnToggle={showColumnToggle}
        showGlobalFilter={showGlobalFilter}
        noDataMessage='No data found'
        searchKey=''
        title={hasLabel ? renderCompTitle() : ''}
        searchBoxCornerRadius={searchCorner}
        searchBoxWidth={searchBoxWidth}
        parentBackgroundColor={parentBackgroundColor}
        spacingTable={convertToPhysicalMargin(spacingTable)}
        columnFilterColor={searchColumnFilterColor}
        searchBoxColor={searchColor}
        showCheckbox={isRowSelectionActive}
        onCheckboxChange={(row: unknown, isChecked: boolean) => onItemSelect((row as any)._id, isChecked)}
        rowIdField='_id'
        onRowMappingChange={handleRowMappingChange}
        controlledRowSelection={getControlledSelection}
        customPagination={renderCustomPagination()}
        onSortChange={handleSortChange}
        devMode={isDevelopmentMode}
        // add button
        showAddButton={showAddButton}
        addButtonColorVariant={addButtonColor}
        addButtonCornerRadius={addButtonCornerRadius}
        addButtonPosition={addButtonPosition}
        onAddButtonClick={handleAddButtonClick}
        // hoverEffect={hoverEffect}
        // hoverIntensity={hoverIntensity}
      />
    </div>
  )
}
